import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Button from 'Components/Atoms/Button/Button';
import Icon from 'Components/Atoms/Icon/Icon';
import { createProviderConnectionManual } from 'Services/providersConnections';
import { useErrors } from 'Components/Organisms/ErrorsProvider';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { MANUAL_PROVIDER_CONNECTOR_ID } from 'Utils/constants';
import CreateTransactionDrawerAddProviderRow from './CreateTransactionDrawerAddProviderRow';
import FormProvider, { RHFInput } from 'Components/Organisms/HookForm';

const INITIAL_PRODUCT = {
    name: '',
    type: 'cash',
    currency: 'EUR',
};

export default function CreateTransactionDrawerAddProvider(props) {
    const { onSubmit, onClose } = props;
    const [submitting, setSubmitting] = useState(false);
    const { showUnknownError } = useErrors();
    const queryClient = useQueryClient();

    const { t } = useTranslation('common');

    const schema = yup.object().shape({
        name: yup.string().required(t('manual-bank.name-is-required')),
        products: yup
            .array()
            .min(1, t('manual-bank.product-is-required'))
            .of(
                yup.object().shape({
                    name: yup.string().required(t('manual-bank.name-is-required')),
                    type: yup.string(),
                    currency: yup.string(),
                }),
            ),
    });

    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            name: '',
            products: [INITIAL_PRODUCT],
        },
    });

    const { handleSubmit, control } = methods;
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'products',
    });

    const submit = async (data) => {
        setSubmitting(true);
        try {
            const response = await createProviderConnectionManual({
                connectorId: MANUAL_PROVIDER_CONNECTOR_ID,
                name: data.name,
                products: data.products,
            });
            await queryClient.invalidateQueries({ queryKey: ['providers-connections'] });

            onSubmit(response);
        } catch (error) {
            onClose();
            showUnknownError(error);
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        const modal = document.querySelector('.modal-dialog');

        if (modal) {
            modal.classList.remove('modal-md');
            modal.classList.add('modal-xl');
        }

        return () => {
            if (modal) {
                modal.classList.remove('modal-xl');
                modal.classList.add('modal-md');
            }
        };
    }, []);

    return (
        <FormProvider className="flex flex-col h-full" onSubmit={handleSubmit(submit)} methods={methods}>
            <div className="grow">
                <div className="mb-6">
                    <div className="text-gray-500">{t('manual-bank.new-manual-bank-description')}</div>
                </div>

                <div className="py-3">
                    <RHFInput name="name" label={t('manual-bank.bank-name')} data-autofocus="true" />
                </div>

                <div className="mb-12">
                    <div className="uppercase text-2xs font-semibold text-gray-500 tracking-wide py-2">
                        {t('manual-bank.products')}
                    </div>
                    {fields.length === 0 && (
                        <div className="py-4 px-2 pb-6 text-center text-xs text-gray-400 tracking-wide">
                            {t('manual-bank.add-at-least-one-product')}
                        </div>
                    )}
                    {fields.map((field, index) => (
                        <CreateTransactionDrawerAddProviderRow
                            key={field.id}
                            index={index}
                            onRemove={() => remove(index)}
                        />
                    ))}
                    <div>
                        <button
                            type="button"
                            className="bg-white text-gray-900 border-0 text-sm outline-none cursor-pointer flex items-center justify-center transition-colors duration-200 py-2 px-3 gap-2 font-medium rounded hover:bg-gray-100"
                            onClick={() => append(INITIAL_PRODUCT)}
                        >
                            <Icon name="circle-plus" color="currentColor" size={20} />
                            {t('manual-bank.add-product')}
                        </button>
                    </div>
                </div>
            </div>

            <div className="sticky bottom-0 bg-white pt-2 pb-4 z-10">
                <div className="flex gap-2 justify-evenly">
                    <Button
                        isBusy={submitting}
                        variant="primary"
                        className="grow"
                        type="submit"
                        testId="create-transaction-add-provider.create"
                    >
                        {t('manual-bank.connect')}
                    </Button>
                    <Button onClick={onClose} className="grow" testId="create-transaction-add-provider.cancel">
                        {t('common:cancel')}
                    </Button>
                </div>
            </div>
        </FormProvider>
    );
}

CreateTransactionDrawerAddProvider.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};
